import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "react-redux";
import { useNotifications } from "@its-engineering-and-development/ui-foundation/providers";
import { useAppDispatch, type RootState } from "@app/store";
import { PERMISSION_ACCESS, ROLE_KEY } from "@app/types/permissions";
import type { RoleKey } from "@app/types/permissions";
import type { ModulePermissionCell, PermissionAccess, PermissionModule, PermissionRole, PermissionUser, RoleAssignment } from "@app/types/permissions";
import { makeGql } from "@app/utils/gql";

const GRAPHQL_ENDPOINT = import.meta.env.VITE_GRAPHQL_ENDPOINT ?? "";
const APPSYNC_API_KEY = import.meta.env.VITE_GRAPHQL_API_KEY ?? "";
// Role card preview always requests this many users; the service applies the limit, not the client.
const ROLE_PREVIEW_USER_LIMIT = 5;

// Roles listed here are shown first in this order; roles added by the service appear afterward.
export const PERMISSION_ROLE_DISPLAY_ORDER = [
    ROLE_KEY.ExpenseAdmin,
    ROLE_KEY.CompanyAccountingApprover,
    ROLE_KEY.GroupAccountingApprover,
    ROLE_KEY.AccountingAdmin,
    ROLE_KEY.Approver,
] as const;
const PERMISSION_ROLE_ORDER_INDEX: ReadonlyMap<string, number> = new Map(PERMISSION_ROLE_DISPLAY_ORDER.map((roleKey, index) => [roleKey, index]));

// ─── GQL infrastructure ──────────────────────────────────────────────────────

function useApiHeaders() {
    return useCallback((): Record<string, string> => {
        const h: Record<string, string> = {};
        if (APPSYNC_API_KEY) h["x-api-key"] = APPSYNC_API_KEY;
        return h;
    }, []);
}

// ─── GraphQL documents ────────────────────────────────────────────────────────

const GET_PERMISSIONS_PAGE_DATA_QUERY = `
  query GetPermissionsPageData {
    roles(isSystemDefined: true) {
      roleId
      roleKey
      roleName
      preferedRoleName
      description
      isSystemDefined
      isActive
      permissionsDetails {
        moduleKey
        moduleName
        permission
        isConfigurable
      }
    }
    modules {
      moduleKey
      moduleName
      isActive
      parentModuleKey
    }
  }
`;

// Step 1 — full assigned-user-id list for a role (CAS only, no user details yet).
const ROLE_USER_ASSIGNMENTS_QUERY = `
  query RoleUserAssignments($roleId: ID!) {
    roleUserAssignments(roleId: $roleId) {
      userId
    }
  }
`;

// Step 2/3 — hydrate user details for a set of assigned ids. Same query for every role
// (card preview: first 5 ids; Manage users drawer: current page's ids). The Manage users
// table also passes `filter` (name/email) and optional `sortBy`/`sortOrder` (server-side).
const PERMISSIONS_USERS_BY_BATCH_QUERY = `
  query PermissionsUsersByBatch($userIds: [ID!]!, $limit: Int, $cursor: String, $filter: UsersByBatchFilter, $sortBy: String, $sortOrder: String) {
    permissionsUsersByBatch(userIds: $userIds, limit: $limit, cursor: $cursor, filter: $filter, sortBy: $sortBy, sortOrder: $sortOrder) {
      items {
        userId
        legalName {
          firstName
          lastName
        }
        emailId
      }
      pageInfo {
        limit
        hasMore
        nextCursor
      }
    }
  }
`;

const UPDATE_MODULE_PERMISSIONS_MUTATION = `
  mutation UpdateModulePermissions($input: UpdateModulePermissionsInput!) {
    updateModulePermissions(input: $input) {
      roleId
      permissionsDetails {
        moduleKey
        permission
        isConfigurable
      }
    }
  }
`;

const ASSIGN_USER_ROLE_MUTATION = `
  mutation AssignUserRole($roleId: ID!, $userId: ID!) {
    assignUserRole(roleId: $roleId, userId: $userId) {
      userId
      roleId
      companyId
      assignedDate
    }
  }
`;

const UNASSIGN_USER_ROLE_MUTATION = `
  mutation UnassignUserRole($roleId: ID!, $userId: ID!) {
    unassignUserRole(roleId: $roleId, userId: $userId)
  }
`;

const GET_USERS_QUERY = `
  query GetUsersPage($limit: Int, $cursor: String, $name: String) {
    permissionsUsers(limit: $limit, cursor: $cursor, name: $name) {
      pageInfo {
        cursor
        hasNextPage
      }
      users {
        userId
        legalName {
          firstName
          lastName
        }
        emailId
      }
    }
  }
`;

// ─── BFF response types ───────────────────────────────────────────────────────

interface BffPermissionDetail {
    moduleKey: string;
    moduleName: string;
    permission: string;
    isConfigurable: boolean;
}

interface BffRole {
    roleId: string;
    roleKey: string | null;
    roleName: string;
    preferedRoleName: string | null;
    description: string | null;
    isSystemDefined: boolean;
    isActive: boolean;
    permissionsDetails: BffPermissionDetail[];
}

interface BffModule {
    moduleKey: string;
    moduleName: string;
    isActive: boolean;
    parentModuleKey: string | null;
}

interface BffUser {
    userId: string;
    legalName: { firstName: string | null; lastName: string | null } | null;
    emailId?: string | null;
}

interface GetPermissionsPageDataResponse {
    roles: BffRole[];
    modules: BffModule[];
}

export function sortPermissionRoles<T extends { roleKey: string | null }>(roles: T[]): T[] {
    return roles
        .map((role, sourceIndex) => ({ role, sourceIndex }))
        .sort((left, right) => {
            const leftIndex = PERMISSION_ROLE_ORDER_INDEX.get(left.role.roleKey ?? "") ?? Number.MAX_SAFE_INTEGER;
            const rightIndex = PERMISSION_ROLE_ORDER_INDEX.get(right.role.roleKey ?? "") ?? Number.MAX_SAFE_INTEGER;

            return leftIndex - rightIndex || left.sourceIndex - right.sourceIndex;
        })
        .map(({ role }) => role);
}

interface RoleUserAssignmentsResponse {
    roleUserAssignments: { userId: string }[];
}

interface PermissionsUsersByBatchResponse {
    permissionsUsersByBatch: {
        items: BffUser[];
        pageInfo: { limit: number; hasMore: boolean; nextCursor: string | null };
    };
}

interface BffUsersPageInfo {
    cursor: string | null;
    hasNextPage: boolean;
}

interface BffUsersQueryResponse {
    permissionsUsers: {
        pageInfo: BffUsersPageInfo;
        users: BffUser[];
    };
}

interface BffUpdatePermissionsDetail {
    moduleKey: string;
    permission: string;
    isConfigurable: boolean;
}

interface BffUpdatePermissionsResponse {
    updateModulePermissions: {
        roleId: string;
        permissionsDetails: BffUpdatePermissionsDetail[];
    };
}

interface BffAssignUserResponse {
    assignUserRole: { userId: string; roleId: string; companyId: string; assignedDate: string };
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface BffUnassignUserResponse {}

// ─── Mapping helpers ──────────────────────────────────────────────────────────

const PERMISSION_LEVEL_MAP: Record<string, PermissionAccess> = {
    None: "no-access",
    Read: "read-only",
    Read_Write: "read-write",
};

const ACCESS_TO_BFF_PERMISSION_MAP: Record<PermissionAccess, string> = {
    [PERMISSION_ACCESS.NoAccess]: "None",
    [PERMISSION_ACCESS.ReadOnly]: "Read",
    [PERMISSION_ACCESS.ReadWrite]: "Read_Write",
};

function mapPermission(level: string): PermissionAccess {
    return PERMISSION_LEVEL_MAP[level] ?? "no-access";
}

function buildDisplayName(user: BffUser): string {
    const name = [user.legalName?.firstName, user.legalName?.lastName].filter(Boolean).join(" ").trim();
    // Fall back to an identifier so aria-labels/table cells never render blank.
    return name || user.emailId || user.userId;
}

function buildInitials(user: BffUser): string {
    const first = (user.legalName?.firstName ?? "").charAt(0);
    const last = (user.legalName?.lastName ?? "").charAt(0);
    const initials = (first + last).toUpperCase();
    if (initials) return initials;
    return (user.emailId ?? user.userId).slice(0, 2).toUpperCase();
}

function mapBffUser(user: BffUser): PermissionUser {
    return {
        id: user.userId,
        name: buildDisplayName(user),
        initials: buildInitials(user),
        emailId: user.emailId ?? null,
    };
}

// Modules that should never appear in the permissions matrix UI
const enum HiddenModuleKey {
    ExpenseApprovals = "expense_approvals",
}

// Subtitles keyed by stable roleKey — fields not present in the API schema
const ROLE_SUBTITLES: Partial<Record<RoleKey, string>> = {
    [ROLE_KEY.GroupAccountingApprover]: "Report visibility: assigned groups only",
};

// Build a module tree by parsing the " / " separator in moduleName.
// e.g. "Policy / Currency" → parent { id: "policy", label: "Policy" }, child { id: "policy_currency", label: "Currency" }
// Modules without " / " are top-level entries rendered in the order returned by the API.
function buildModuleTree(modules: BffModule[]): PermissionModule[] {
    const active = modules.filter((m) => m.isActive && m.moduleKey !== HiddenModuleKey.ExpenseApprovals);
    const result: PermissionModule[] = [];
    const syntheticParents = new Map<string, PermissionModule>();

    for (const m of active) {
        const sep = m.moduleName.indexOf(" / ");
        if (sep !== -1) {
            const parentLabel = m.moduleName.slice(0, sep);
            const childLabel = m.moduleName.slice(sep + 3);
            const parentKey = parentLabel.toLowerCase();

            if (!syntheticParents.has(parentKey)) {
                const parent: PermissionModule = { id: parentKey, label: parentLabel, children: [] };
                syntheticParents.set(parentKey, parent);
                result.push(parent);
            }
            syntheticParents.get(parentKey)!.children!.push({ id: m.moduleKey, label: childLabel });
        } else {
            result.push({ id: m.moduleKey, label: m.moduleName });
        }
    }

    return result;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePermissions() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);
    const { showError } = useNotifications();

    const [bffRoles, setBffRoles] = useState<BffRole[]>([]);
    const [bffModules, setBffModules] = useState<BffModule[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [roleAssignedIds, setRoleAssignedIds] = useState<Map<string, string[]>>(new Map());
    const [rolePreviewUsers, setRolePreviewUsers] = useState<Map<string, BffUser[]>>(new Map());
    const [loadingAssignmentIds, setLoadingAssignmentIds] = useState<Set<string>>(new Set());
    const [loadingPreviewIds, setLoadingPreviewIds] = useState<Set<string>>(new Set());
    const [roleUserErrors, setRoleUserErrors] = useState<Map<string, string>>(new Map());
    const [pendingCells, setPendingCells] = useState<Set<string>>(new Set());
    const [optimisticOverrides, setOptimisticOverrides] = useState<Map<string, ModulePermissionCell>>(new Map());

    const [pendingUserIds, setPendingUserIds] = useState<Map<string, Set<string>>>(new Map());
    const [optimisticAddedByRole, setOptimisticAddedByRole] = useState<Map<string, Set<string>>>(new Map());
    // Roles with an assign round trip in flight — covers assignUserRole AND the preview
    // refetch it triggers, so the loader doesn't drop between the two calls.
    const [assigningRoleIds, setAssigningRoleIds] = useState<Set<string>>(new Set());

    const [allFetchedUsers, setAllFetchedUsers] = useState<BffUser[]>([]);
    const [usersCursor, setUsersCursor] = useState<string | null>(null);
    const [hasNextUsersPage, setHasNextUsersPage] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    const phase1Inflight = useRef(false);
    const assignmentsInflight = useRef<Set<string>>(new Set());
    const assignmentsCompleted = useRef<Set<string>>(new Set());
    const previewInflight = useRef<Set<string>>(new Set());
    const previewFetchedKey = useRef<Map<string, string>>(new Map());
    const usersInitialLoaded = useRef(false);

    // Phase 1 — load roles + modules on mount
    useEffect(() => {
        if (phase1Inflight.current) return;
        phase1Inflight.current = true;
        setIsLoading(true);
        setError(null);

        gql<GetPermissionsPageDataResponse>(GET_PERMISSIONS_PAGE_DATA_QUERY)
            .then((data) => {
                setBffRoles(sortPermissionRoles(data.roles.filter((r) => r.isActive)));
                setBffModules(data.modules);
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : "Failed to load permissions data");
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [gql]);

    // Users list — load first page on mount for the assign-user dropdowns
    useEffect(() => {
        if (usersInitialLoaded.current) return;
        usersInitialLoaded.current = true;
        setIsLoadingUsers(true);

        gql<BffUsersQueryResponse>(GET_USERS_QUERY, { limit: 50 })
            .then((data) => {
                setAllFetchedUsers(data.permissionsUsers.users);
                setUsersCursor(data.permissionsUsers.pageInfo.cursor ?? null);
                setHasNextUsersPage(data.permissionsUsers.pageInfo.hasNextPage);
            })
            .catch((err: unknown) => {
                // Non-blocking: the assign-user dropdown will show an empty list.
                console.error("[use-permissions] Failed to load initial users page:", err);
            })
            .finally(() => {
                setIsLoadingUsers(false);
            });
    }, [gql]);

    // Phase 2, step 1 — load the full assigned-user-id list per role. Same query/shape for
    // every role — no branching by roleKey (e.g. Group Accounting Approver is no longer special).
    useEffect(() => {
        if (bffRoles.length === 0) return;

        for (const role of bffRoles) {
            const { roleId } = role;
            if (assignmentsInflight.current.has(roleId)) continue;
            if (assignmentsCompleted.current.has(roleId)) continue;
            assignmentsInflight.current.add(roleId);

            setLoadingAssignmentIds((prev) => new Set(prev).add(roleId));

            gql<RoleUserAssignmentsResponse>(ROLE_USER_ASSIGNMENTS_QUERY, { roleId })
                .then((data) => {
                    setRoleAssignedIds((prev) =>
                        new Map(prev).set(
                            roleId,
                            data.roleUserAssignments.map((a) => a.userId),
                        ),
                    );
                })
                .catch((err: unknown) => {
                    const msg = err instanceof Error ? err.message : "Failed to load users";
                    setRoleUserErrors((prev) => new Map(prev).set(roleId, msg));
                })
                .finally(() => {
                    assignmentsInflight.current.delete(roleId);
                    assignmentsCompleted.current.add(roleId);
                    setLoadingAssignmentIds((prev) => {
                        const next = new Set(prev);
                        next.delete(roleId);
                        return next;
                    });
                });
        }
    }, [bffRoles, gql]);

    // Phase 2, step 2 — hydrate the card preview users per role, re-running whenever that
    // role's assigned-id list changes (e.g. after assign/unassign). The full id list is sent
    // on every call; the fixed limit (not the id count) determines how many come back.
    useEffect(() => {
        const stopAssigning = (roleId: string) =>
            setAssigningRoleIds((prev) => {
                if (!prev.has(roleId)) return prev;
                const next = new Set(prev);
                next.delete(roleId);
                return next;
            });

        for (const [roleId, ids] of roleAssignedIds.entries()) {
            const key = ids.join(",");
            if (previewFetchedKey.current.get(roleId) === key) {
                // Preview already matches this id list (e.g. assignUser resolved as a no-op) — nothing to refetch.
                stopAssigning(roleId);
                continue;
            }
            if (previewInflight.current.has(roleId)) continue;

            if (ids.length === 0) {
                previewFetchedKey.current.set(roleId, key);
                setRolePreviewUsers((prev) => new Map(prev).set(roleId, []));
                stopAssigning(roleId);
                continue;
            }

            previewInflight.current.add(roleId);
            setLoadingPreviewIds((prev) => new Set(prev).add(roleId));

            gql<PermissionsUsersByBatchResponse>(PERMISSIONS_USERS_BY_BATCH_QUERY, { userIds: ids, limit: ROLE_PREVIEW_USER_LIMIT })
                .then((data) => {
                    previewFetchedKey.current.set(roleId, key);
                    setRolePreviewUsers((prev) => new Map(prev).set(roleId, data.permissionsUsersByBatch.items));
                })
                .catch((err: unknown) => {
                    console.error("[use-permissions] Failed to load role preview users:", err);
                })
                .finally(() => {
                    previewInflight.current.delete(roleId);
                    setLoadingPreviewIds((prev) => {
                        const next = new Set(prev);
                        next.delete(roleId);
                        return next;
                    });
                    stopAssigning(roleId);
                });
        }
    }, [roleAssignedIds, gql]);

    // A role stays "loading" (preview skeleton) from the moment assignUser is called until its
    // new preview has been fetched, not just while the permissionsUsersByBatch call is in flight.
    const loadingRoleIds = useMemo(
        () => new Set([...loadingAssignmentIds, ...loadingPreviewIds, ...assigningRoleIds]),
        [loadingAssignmentIds, loadingPreviewIds, assigningRoleIds],
    );

    const fetchNextUsersPage = useCallback(() => {
        if (!hasNextUsersPage || isLoadingUsers) return;
        setIsLoadingUsers(true);

        gql<BffUsersQueryResponse>(GET_USERS_QUERY, { limit: 50, cursor: usersCursor })
            .then((data) => {
                setAllFetchedUsers((prev) => [...prev, ...data.permissionsUsers.users]);
                setUsersCursor(data.permissionsUsers.pageInfo.cursor ?? null);
                setHasNextUsersPage(data.permissionsUsers.pageInfo.hasNextPage);
            })
            .catch((err: unknown) => {
                console.error("[use-permissions] Failed to load next users page:", err);
            })
            .finally(() => {
                setIsLoadingUsers(false);
            });
    }, [gql, hasNextUsersPage, isLoadingUsers, usersCursor]);

    const fetchUsersByName = useCallback(
        (name: string): Promise<PermissionUser[]> =>
            gql<BffUsersQueryResponse>(GET_USERS_QUERY, { limit: 50, name }).then((data) => data.permissionsUsers.users.map(mapBffUser)),
        [gql],
    );

    // ─── Derived UI state ─────────────────────────────────────────────────────

    // bffRoles is already sorted by PERMISSION_ROLE_DISPLAY_ORDER at load time, so the assignment
    // cards reuse that order and stay consistent with the permissions matrix.
    const roleAssignments = useMemo<RoleAssignment[]>(
        () =>
            bffRoles.map((r) => {
                const confirmedIds = roleAssignedIds.get(r.roleId) ?? [];
                const optimisticIds = Array.from(optimisticAddedByRole.get(r.roleId) ?? new Set<string>());
                const userIds = [...new Set([...confirmedIds, ...optimisticIds])];
                return { roleId: r.roleId, description: r.description ?? "", userIds };
            }),
        [bffRoles, roleAssignedIds, optimisticAddedByRole],
    );

    const rolePreviewUsersByRoleId = useMemo<Map<string, PermissionUser[]>>(
        () => new Map(Array.from(rolePreviewUsers.entries()).map(([roleId, users]) => [roleId, users.map(mapBffUser)])),
        [rolePreviewUsers],
    );

    const roles = useMemo<PermissionRole[]>(
        () =>
            bffRoles.map((r) => ({
                id: r.roleId,
                roleKey: r.roleKey ?? undefined,
                label: r.preferedRoleName ?? r.roleName,
                subtitle: ROLE_SUBTITLES[r.roleKey as RoleKey],
            })),
        [bffRoles],
    );

    const modules = useMemo<PermissionModule[]>(() => buildModuleTree(bffModules), [bffModules]);

    const permissionMap = useMemo(() => {
        const map = new Map<string, ModulePermissionCell>();
        for (const role of bffRoles) {
            for (const detail of role.permissionsDetails) {
                map.set(`${detail.moduleKey}:${role.roleId}`, {
                    moduleId: detail.moduleKey,
                    roleId: role.roleId,
                    access: mapPermission(detail.permission),
                    editable: detail.isConfigurable,
                });
            }
        }
        return map;
    }, [bffRoles]);

    const getPermission = useCallback(
        (moduleId: string, roleId: string) => {
            const key = `${moduleId}:${roleId}`;
            return optimisticOverrides.get(key) ?? permissionMap.get(key) ?? null;
        },
        [optimisticOverrides, permissionMap],
    );

    const users = useMemo<PermissionUser[]>(() => allFetchedUsers.map(mapBffUser), [allFetchedUsers]);

    const updatePermission = useCallback(
        (moduleId: string, roleId: string, access: PermissionAccess) => {
            const key = `${moduleId}:${roleId}`;
            const prevCell = permissionMap.get(key) ?? null;

            // Optimistic update
            setOptimisticOverrides((map) => {
                const next = new Map(map);
                next.set(key, { moduleId, roleId, access, editable: prevCell?.editable ?? true });
                return next;
            });
            setPendingCells((set) => new Set(set).add(key));

            gql<BffUpdatePermissionsResponse>(UPDATE_MODULE_PERMISSIONS_MUTATION, {
                input: { roleId, moduleKey: moduleId, permission: ACCESS_TO_BFF_PERMISSION_MAP[access] },
            })
                .then((data) => {
                    setBffRoles((prev) =>
                        prev.map((r) => {
                            if (r.roleId !== data.updateModulePermissions.roleId) return r;
                            // Merge: keep all existing details, overlay only the entries returned by the mutation
                            const mergedByKey = new Map(r.permissionsDetails.map((d) => [d.moduleKey, d]));
                            for (const d of data.updateModulePermissions.permissionsDetails) {
                                mergedByKey.set(d.moduleKey, {
                                    moduleKey: d.moduleKey,
                                    moduleName: mergedByKey.get(d.moduleKey)?.moduleName ?? d.moduleKey,
                                    permission: d.permission,
                                    isConfigurable: d.isConfigurable,
                                });
                            }
                            return { ...r, permissionsDetails: Array.from(mergedByKey.values()) };
                        }),
                    );
                    setOptimisticOverrides((map) => {
                        const next = new Map(map);
                        next.delete(key);
                        return next;
                    });
                })
                .catch(() => {
                    // Roll back optimistic update on error
                    setOptimisticOverrides((map) => {
                        const next = new Map(map);
                        if (prevCell) next.set(key, prevCell);
                        else next.delete(key);
                        return next;
                    });
                })
                .finally(() => {
                    setPendingCells((set) => {
                        const next = new Set(set);
                        next.delete(key);
                        return next;
                    });
                });
        },
        [gql, permissionMap],
    );
    const assignUser = useCallback(
        (roleId: string, userId: string) => {
            const addToPending = (map: Map<string, Set<string>>) => {
                const next = new Map(map);
                next.set(roleId, new Set([...(next.get(roleId) ?? []), userId]));
                return next;
            };
            const removeFromPending = (map: Map<string, Set<string>>) => {
                const next = new Map(map);
                const s = new Set(next.get(roleId) ?? []);
                s.delete(userId);
                if (s.size === 0) next.delete(roleId);
                else next.set(roleId, s);
                return next;
            };

            setOptimisticAddedByRole(addToPending);
            setPendingUserIds(addToPending);
            setAssigningRoleIds((prev) => new Set(prev).add(roleId));

            gql<BffAssignUserResponse>(ASSIGN_USER_ROLE_MUTATION, { roleId, userId })
                .then(() => {
                    setRoleAssignedIds((prev) => {
                        const next = new Map(prev);
                        const existing = next.get(roleId) ?? [];
                        if (!existing.includes(userId)) next.set(roleId, [...existing, userId]);
                        return next;
                    });
                    setOptimisticAddedByRole(removeFromPending);
                })
                .catch(() => {
                    setOptimisticAddedByRole(removeFromPending);
                    // No id was added, so the preview effect won't run for this role — stop loading here.
                    setAssigningRoleIds((prev) => {
                        if (!prev.has(roleId)) return prev;
                        const next = new Set(prev);
                        next.delete(roleId);
                        return next;
                    });
                })
                .finally(() => {
                    setPendingUserIds(removeFromPending);
                });
        },
        [gql],
    );

    const unassignUser = useCallback(
        (roleId: string, userId: string) => {
            const addToPending = (map: Map<string, Set<string>>) => {
                const next = new Map(map);
                next.set(roleId, new Set([...(next.get(roleId) ?? []), userId]));
                return next;
            };
            const removeFromPending = (map: Map<string, Set<string>>) => {
                const next = new Map(map);
                const s = new Set(next.get(roleId) ?? []);
                s.delete(userId);
                if (s.size === 0) next.delete(roleId);
                else next.set(roleId, s);
                return next;
            };

            setPendingUserIds(addToPending);

            return gql<BffUnassignUserResponse>(UNASSIGN_USER_ROLE_MUTATION, { roleId, userId })
                .then(() => {
                    setRoleAssignedIds((prev) => {
                        const next = new Map(prev);
                        next.set(
                            roleId,
                            (next.get(roleId) ?? []).filter((id) => id !== userId),
                        );
                        return next;
                    });
                })
                .catch(() => {
                    showError("Could not remove user", "Something went wrong. Please try again.");
                })
                .finally(() => {
                    setPendingUserIds(removeFromPending);
                });
        },
        [gql, showError],
    );

    return {
        roles,
        modules,
        users,
        roleAssignments,
        rolePreviewUsersByRoleId,
        getPermission,
        updatePermission,
        assignUser,
        unassignUser,
        isLoading,
        loadingRoleIds,
        pendingCells,
        pendingUserIds,
        isLoadingUsers,
        hasNextUsersPage,
        fetchNextUsersPage,
        fetchUsersByName,
        error,
        roleUserErrors,
    };
}

// ─── Manage users drawer (Step 3) ────────────────────────────────────────────
// Paginates a role's full assigned-user-id list 25-at-a-time via permissionsUsersByBatch.
// The service supports `filter: { name, email }` and `sortBy`/`sortOrder`, both applied
// server-side before pagination. When a filter or a sort is active the whole assigned set is
// paginated server-side via cursor; with neither, ids are sliced client-side page-by-page.

export interface PermissionsTablePageInfo {
    totalCount: number;
    offset: number;
    limit: number;
    hasNextPage: boolean;
}

export interface PermissionsUsersByBatchFilter {
    name?: string;
    email?: string;
}

export type PermissionsUsersSortField = "name" | "email";

export interface PermissionsUsersByBatchSort {
    sortBy: PermissionsUsersSortField;
    sortOrder: "asc" | "desc";
}

/** Next sort state for a header click: activate a field asc, or flip asc↔desc when it's already active. */
export function toggleUsersSort(current: PermissionsUsersByBatchSort | undefined, field: PermissionsUsersSortField): PermissionsUsersByBatchSort {
    const sortOrder = current?.sortBy === field && current.sortOrder === "asc" ? "desc" : "asc";
    return { sortBy: field, sortOrder };
}

export function usePermissionsRoleUsersTable(
    userIds: string[],
    limit: number = 25,
    filter?: PermissionsUsersByBatchFilter,
    sort?: PermissionsUsersByBatchSort,
) {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    const idsKey = userIds.join(",");
    const hasFilter = Boolean(filter?.name || filter?.email);
    const hasSort = Boolean(sort);
    // Sorting reorders the whole set, so (like filtering) it can't use the client-side id slice.
    const useServerPagination = hasFilter || hasSort;
    const filterKey = `${filter?.name ?? ""}|${filter?.email ?? ""}`;
    const sortKey = sort ? `${sort.sortBy}:${sort.sortOrder}` : "";
    const [currentPage, setCurrentPage] = useState(0);
    const [pageUsers, setPageUsers] = useState<BffUser[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMoreServer, setHasMoreServer] = useState(false);
    const inflightRef = useRef<Set<string>>(new Set());
    const prevIdsKeyRef = useRef(idsKey);
    const prevFilterKeyRef = useRef(filterKey);
    const prevSortKeyRef = useRef(sortKey);
    // Cursor for each page, only used while server-side pagination is active (server doesn't know
    // about our full id list's index-based pages, so we can't jump to an arbitrary
    // offset — we walk forward/back via the cursor each page's response returned).
    const cursorHistoryRef = useRef<Record<number, string | null>>({ 0: null });

    useEffect(() => {
        if (prevIdsKeyRef.current !== idsKey || prevFilterKeyRef.current !== filterKey || prevSortKeyRef.current !== sortKey) {
            prevIdsKeyRef.current = idsKey;
            prevFilterKeyRef.current = filterKey;
            prevSortKeyRef.current = sortKey;
            cursorHistoryRef.current = { 0: null };
            setCurrentPage(0);
        }
    }, [idsKey, filterKey, sortKey]);

    const totalCount = userIds.length;
    const pageIds = useMemo(() => userIds.slice(currentPage * limit, currentPage * limit + limit), [idsKey, currentPage, limit]); // eslint-disable-line react-hooks/exhaustive-deps
    const pageIdsKey = pageIds.join(",");

    useEffect(() => {
        if (useServerPagination) {
            const cursor = cursorHistoryRef.current[currentPage] ?? null;
            const key = `perm-table-server-${idsKey}-${filterKey}-${sortKey}-${currentPage}-${limit}`;
            if (inflightRef.current.has(key)) return;
            inflightRef.current.add(key);
            setIsLoading(true);

            gql<PermissionsUsersByBatchResponse>(PERMISSIONS_USERS_BY_BATCH_QUERY, {
                userIds,
                limit,
                cursor,
                filter,
                sortBy: sort?.sortBy,
                sortOrder: sort?.sortOrder,
            })
                .then((data) => {
                    setPageUsers(data.permissionsUsersByBatch.items);
                    setHasMoreServer(data.permissionsUsersByBatch.pageInfo.hasMore);
                    cursorHistoryRef.current[currentPage + 1] = data.permissionsUsersByBatch.pageInfo.nextCursor;
                })
                .catch((err: unknown) => {
                    console.error("[usePermissionsRoleUsersTable] Failed to load server-paginated users page:", err);
                })
                .finally(() => {
                    inflightRef.current.delete(key);
                    setIsLoading(false);
                });
            return;
        }

        if (pageIds.length === 0) {
            setPageUsers([]);
            return;
        }
        const key = `perm-table-${pageIdsKey}-${limit}`;
        if (inflightRef.current.has(key)) return;
        inflightRef.current.add(key);
        setIsLoading(true);

        gql<PermissionsUsersByBatchResponse>(PERMISSIONS_USERS_BY_BATCH_QUERY, { userIds: pageIds, limit })
            .then((data) => setPageUsers(data.permissionsUsersByBatch.items))
            .catch((err: unknown) => {
                console.error("[usePermissionsRoleUsersTable] Failed to load users page:", err);
            })
            .finally(() => {
                inflightRef.current.delete(key);
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useServerPagination, filterKey, sortKey, idsKey, pageIdsKey, currentPage, limit, gql]);

    const pageInfo: PermissionsTablePageInfo = hasFilter
        ? {
              // The service doesn't report a filtered total count, so this is an
              // estimate that keeps "Page N of M" reasonable without under-reporting.
              totalCount: currentPage * limit + pageUsers.length + (hasMoreServer ? 1 : 0),
              offset: currentPage * limit,
              limit,
              hasNextPage: hasMoreServer,
          }
        : {
              // Sorting (no filter) reorders but keeps the full set, so the real count is known.
              totalCount,
              offset: currentPage * limit,
              limit,
              hasNextPage: (currentPage + 1) * limit < totalCount,
          };

    return {
        users: useMemo(() => pageUsers.map(mapBffUser), [pageUsers]),
        isLoading,
        currentPage,
        goToPage: setCurrentPage,
        pageInfo,
    };
}
